import { type ReactNode, useEffect, useRef, useState } from 'react'
import { cn } from '../cn'

export interface ScrollRegionProps {
  /** Announced when the content is wider than the region and it becomes a tab stop. */
  label: string
  className?: string
  children: ReactNode
}

/**
 * A horizontally scrolling container (wide tables on phones).
 * - `relative`, so absolutely positioned descendants (`sr-only` captions and labels) are
 *   clipped with the content instead of widening the page at 390 px.
 * - When the content actually overflows, it becomes a focusable, labelled region, so
 *   keyboard users can scroll it (WCAG 2.1.1, axe `scrollable-region-focusable`).
 */
export function ScrollRegion({ label, className, children }: ScrollRegionProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [scrollable, setScrollable] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node) return
    const measure = () => setScrollable(node.scrollWidth > node.clientWidth + 1)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    for (const child of Array.from(node.children)) observer.observe(child)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className={cn('relative overflow-x-auto', className)}
      {...(scrollable ? { tabIndex: 0, role: 'region', 'aria-label': label } : {})}
    >
      {children}
    </div>
  )
}
